// The program editor: CodeMirror 6 with Blitz highlighting, completion and
// parameter hints from the runtime's commands (manifest.js), and compile and
// runtime errors marked on their lines, and the debugger's breakpoints and
// stopped line. Each of a project's files keeps its own document, with its own
// undo history, error marks and breakpoints. Blip, the coding helper
// (blip/tools.js), edits files through it too, so that Ctrl+Z undoes his
// edits, and marks the lines he changed.
//
// It looks as the desktop IDE's editor does: its colours (index.html's
// --editor-* properties, from modern/ide/blitzide.cpp) and its font, the
// Blitz font, which index.html's --code-* properties choose.

import { EditorView, keymap, lineNumbers, drawSelection, highlightSpecialChars, rectangularSelection,
  showTooltip, tooltips, gutter, GutterMarker, Decoration, runScopeHandlers } from "@codemirror/view";
import { EditorState, EditorSelection, StateField, StateEffect, RangeSet, Compartment, Transaction } from "@codemirror/state";
import { StreamLanguage, HighlightStyle, syntaxHighlighting, indentUnit, bracketMatching,
  indentOnInput, syntaxTree } from "@codemirror/language";
import { tags } from "@lezer/highlight";
import { autocompletion, completionKeymap, closeBrackets, closeBracketsKeymap } from "@codemirror/autocomplete";
import { defaultKeymap, history, historyKeymap, indentWithTab, isolateHistory } from "@codemirror/commands";
import { search, SearchQuery, getSearchQuery, setSearchQuery, searchPanelOpen, openSearchPanel, closeSearchPanel,
  selectNextOccurrence, selectSelectionMatches, highlightSelectionMatches, gotoLine } from "@codemirror/search";
import { setDiagnostics, lintGutter } from "@codemirror/lint";
import { typeName } from "./manifest.js";
import { liveTools } from "./livetools.js";
import { WORD, counted } from "./findfiles.js";
import { fromRoom, cursorField, setCursors } from "./buddies/binding.js";

// The language's keywords (originalAlphaTokes and extendedAlphaTokes in
// compiler/toker.cpp), by their lower-case spelling, and Extended mode's
// built-in constants, which build.mjs builds in from the compiler's table
// (tools/extended_constants.mjs) as [[NAME, value]]: blitzcc +k gives the
// desktop IDEs the same, as JOB_SUCCEEDED=3.
const ORIGINAL_KEYWORDS = ["After", "And", "Before", "Case", "Const", "Data", "Default", "Delete", "Dialect",
  "Dim", "Each", "Else", "ElseIf", "End", "EndIf", "Exit", "False", "Field", "First", "Float", "For", "Forever",
  "Function", "Global", "Gosub", "Goto", "Handle", "If", "Include", "Insert", "Int", "Last", "Local", "Mod",
  "New", "Next", "Not", "Null", "Object", "Or", "Pi", "Read", "Repeat", "Restore", "Return", "Sar", "Select",
  "Sgn", "Abs", "Shl", "Shr", "Step", "Str", "Then", "To", "True", "Type", "Until", "Wend", "While", "Xor"];
const EXTENDED_KEYWORDS = ["As", "Double", "Enum", "Implements", "Import", "Interface", "Long",
  "Method", "Module", "Private", "Self"];
// Extended words that are keywords only in their place (contextualKeyword in
// modern/ide/editor_lexer.h): List, Map, Set, Queue and Stack after a value
// Type (scores:Int List) and after New, and By after a Map's. Anywhere else
// they are ordinary names.
const CONTEXTUAL_KEYWORDS = new Map(["List", "Map", "Set", "Queue", "Stack", "By"]
  .map((word) => [word.toLowerCase(), word]));
const CONSTANTS = new Map((typeof B3D_EXTENDED_CONSTANTS === "object" ? B3D_EXTENDED_CONSTANTS : [])
  .map(([name, value]) => [name.toLowerCase(), { name, value }]));
const EXTENDED_WORDS = [...EXTENDED_KEYWORDS, ...[...CONSTANTS.values()].map((constant) => constant.name)];
const KEYWORDS = new Map([...ORIGINAL_KEYWORDS, ...EXTENDED_WORDS].map((word) => [word.toLowerCase(), word]));
const EXTENDED_ONLY = new Set(EXTENDED_WORDS.map((word) => word.toLowerCase()));
// Whether a word is one of the language's keywords, in either mode.
export const isKeyword = (word) => KEYWORDS.has(word.toLowerCase());
// A built-in constant's { name, value }, or undefined.
export const constantOf = (word) => CONSTANTS.get(word.toLowerCase());

// Keyword case, as the desktop IDEs correct it (canonicalizeKeywordBeforeCaret
// in modern/ide/editor_control.cpp): typing a space, punctuation, Enter or Tab
// just after a keyword or command spells it as Blitz does, "while" as
// "While" and "graphics3d" as "Graphics3D". Only a word coloured as one is
// changed, never one in a string or comment, and only one the language mode
// has. The correction is part of the same edit, so Ctrl+Z undoes both.
function keywordCase(commands, isExtended) {
  const spelling = (word) => {
    const lower = word.toLowerCase();
    const keyword = KEYWORDS.get(lower);
    if (keyword) return !EXTENDED_ONLY.has(lower) || isExtended() ? keyword : null;
    const contextual = CONTEXTUAL_KEYWORDS.get(lower);
    if (contextual) return isExtended() ? contextual : null;
    const command = commands.get(lower);
    return command && (!command.extended || isExtended()) ? command.name : null;
  };
  return EditorState.transactionFilter.of((tr) => {
    if (!tr.docChanged || !tr.isUserEvent("input") || tr.isUserEvent("input.complete")) return tr;
    let inserted = null;
    tr.changes.iterChanges((fromA, toA, fromB, toB, text) => {
      inserted = inserted === null && fromA === toA ? { at: fromA, text: text.toString() } : false;
    });
    if (!inserted || !/^[\x20-\x2f\x3a-\x40\x5b-\x5e\x60\x7b-\x7e\n\t]/.test(inserted.text)) return tr;
    const state = tr.startState, line = state.doc.lineAt(inserted.at);
    const word = /[A-Za-z_][A-Za-z0-9_]*$/.exec(line.text.slice(0, inserted.at - line.from));
    if (!word) return tr;
    const from = inserted.at - word[0].length;
    const node = syntaxTree(state).resolveInner(from, 1);
    if (node.name !== "keyword" && node.name !== "standard") return tr;
    const correct = spelling(word[0]);
    if (!correct || correct === word[0]) return tr;
    return [tr, { changes: { from, to: inserted.at, insert: correct }, sequential: true }];
  });
}

// A number, as compiler/toker.cpp and modern/ide/editor_lexer.h read one:
// hexadecimal $1F and binary %101, and decimal 12, 1.5 and .5. Extended adds
// a Long's L (1L, $FFL, %1L, not 1.5L) and a Double's D, which may follow an
// exponent (2.5D, 1e3D, 1.5e-3D); without the D, an exponent is not part of
// the number.
function matchNumber(stream, extended) {
  if (stream.match(/^\$[0-9a-f]+/i) || stream.match(/^%[01]+/)) {
    if (extended) stream.match(/^l/i);
    return true;
  }
  const decimal = stream.match(/^(\d+(\.\d*)?|\.\d+)/);
  if (!decimal) return false;
  if (extended) {
    const rest = stream.string.slice(stream.pos);
    const exponent = /^e[+-]?(\d*)/i.exec(rest);
    let end = 0;
    if (exponent && (exponent[1] || /^d/i.test(rest.slice(exponent[0].length)))) end = exponent[0].length;
    if (/^d/i.test(rest.slice(end))) stream.pos += end + 1;
    else if (!decimal[0].includes(".")) stream.match(/^l/i);
  }
  return true;
}

// Blitz, tokenised a line at a time. Extended programs may also comment out
// lines between a line that starts with /* and one that starts with */.
function blitzLanguage(commands, isExtended) {
  // The tokens before this one on the line, for the contextual keywords: a
  // word after a value Type or New, or By after a Map's. A value Type is Int
  // or another type keyword, or a name that is no keyword or command, which
  // keeps Cls : Print list plain.
  const fresh = { previous: "", previousWord: false, previousPlain: false, previousMap: false, beforePrevious: "" };
  const TYPE_KEYWORDS = new Set(["int", "float", "str", "long", "double"]);
  const contextual = (word, state) => word === "by"
    ? state.previousMap
    : state.previousWord && (state.previous === "new" ||
      ((state.beforePrevious === ":" || state.beforePrevious === ".") &&
        (state.previousPlain || TYPE_KEYWORDS.has(state.previous))));
  const tokenOnly = (stream, state) => {
    if (stream.sol()) {
      const rest = stream.string.trimStart();
      if (state.block) {
        if (rest.startsWith("*/")) state.block = false;
        stream.skipToEnd();
        return "comment";
      }
      if (rest.startsWith("/*")) {
        state.block = true;
        stream.skipToEnd();
        return "comment";
      }
    }
    if (stream.eatSpace()) return null;
    if (stream.eat(";")) {
      stream.skipToEnd();
      return "comment";
    }
    if (stream.eat('"')) {
      stream.skipTo('"') ? stream.next() : stream.skipToEnd();
      return "string";
    }
    if (matchNumber(stream, isExtended())) return "number";
    // A label: .name at the start of a line.
    if (stream.column() === stream.indentation() && stream.match(/^\.[A-Za-z_]\w*/)) return "labelName";
    if (stream.match(/^[A-Za-z_]\w*/)) {
      const word = stream.current().toLowerCase();
      // Extended's keywords and constants only in Extended mode, as blitzcc
      // +k gives them to the desktop IDEs.
      if (KEYWORDS.has(word) && (!EXTENDED_ONLY.has(word) || isExtended())) return "keyword";
      if (CONTEXTUAL_KEYWORDS.has(word) && isExtended() && contextual(word, state)) return "keyword";
      if (commands.has(word)) return "standard";
      // Worker is a keyword before Function, as the desktop editor has it.
      if (word === "worker" && /^[ \t]+function\b/i.test(stream.string.slice(stream.pos))) return "keyword";
      return "variableName";
    }
    if (stream.match(/^[%#$&@]/)) return "typeName";
    stream.next();
    return "operator";
  };
  return StreamLanguage.define({
    name: "blitz",
    startState: () => ({ block: false, ...fresh }),
    token(stream, state) {
      if (stream.sol()) Object.assign(state, fresh);
      const style = tokenOnly(stream, state);
      if (style !== null && style !== "comment") {
        const text = stream.current();
        state.beforePrevious = state.previous;
        state.previous = text.toLowerCase();
        state.previousWord = /^[A-Za-z_]/.test(text);
        state.previousPlain = style === "variableName";
        state.previousMap = style === "keyword" && state.previous === "map";
      }
      return style;
    },
    languageData: { commentTokens: { line: ";" } },
    tokenTable: { standard: tags.standard(tags.variableName), labelName: tags.labelName },
  });
}

// The desktop editor's colours: commands are keywords there, labels and type
// marks are names and plain text, and nothing is bold or italic.
const highlight = HighlightStyle.define([
  { tag: tags.comment, color: "var(--editor-comment)" },
  { tag: tags.string, color: "var(--editor-string)" },
  { tag: tags.number, color: "var(--editor-number)" },
  { tag: [tags.keyword, tags.standard(tags.variableName)], color: "var(--editor-keyword)" },
  { tag: [tags.variableName, tags.labelName], color: "var(--editor-identifier)" },
]);

// The editor's text: its scroller, beside its gutters and less its scroll
// bars. Tooltips keep a few pixels inside it, where the splitters beside the
// editor reach in.
const TOOLTIP_INSET = 4;
function textArea(view) {
  const scroller = view.scrollDOM, box = scroller.getBoundingClientRect();
  const gutters = scroller.querySelector(".cm-gutters");
  return { left: box.left + (gutters ? gutters.offsetWidth : 0), top: box.top,
    right: box.left + scroller.clientWidth, bottom: box.top + scroller.clientHeight };
}
// Tooltips (the parameter hint, completions and error messages) stay between
// the top and the bottom of the editor's text, off the file tabs above it and
// the console's bar below it. Across, they may reach past it while you type
// or point, as a completion's details do beside the list; the parameter hint,
// which stays for as long as the cursor is in a call, keeps inside it too.
function tooltipSpace(view) {
  const { top, bottom } = textArea(view);
  return { left: 0, top: top + TOOLTIP_INSET, right: view.dom.ownerDocument.documentElement.clientWidth,
    bottom: bottom - TOOLTIP_INSET };
}

const theme = EditorView.theme({
  "&": { height: "100%", backgroundColor: "var(--editor-background)", color: "var(--editor-text)",
    fontSize: "var(--code-size)" },
  // Text in a layer of its own is smoothed in shades of grey, which keeps the
  // Blitz font's pixels sharp: coloured (subpixel) smoothing fringes them.
  // Layering the scroller, rather than the text in it, keeps them sharp
  // where a CSS pixel is not a whole number of the screen's, too.
  ".cm-scroller": { fontFamily: "var(--code-font)", lineHeight: "var(--code-line)", willChange: "transform" },
  // Whole pixels, so that the Blitz font's lines and columns start on the
  // screen's pixels.
  ".cm-content": { caretColor: "var(--editor-identifier)", padding: "4px 0" },
  ".cm-line": { padding: "0 4px" },
  ".cm-cursor, .cm-dropCursor": { borderLeft: "2px solid var(--editor-identifier)" },
  "&.cm-focused .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection":
    { backgroundColor: "var(--editor-selection)" },
  ".cm-selectionMatch": { backgroundColor: "rgba(0, 120, 212, .45)" },
  "&.cm-focused .cm-matchingBracket": { backgroundColor: "rgba(255, 255, 255, .22)", outline: "none" },
  ".cm-gutters": { backgroundColor: "var(--editor-line-numbers)", color: "var(--editor-line-number)", border: "none" },
  ".cm-lineNumbers .cm-gutterElement": { padding: "0 4px 0 8px", minWidth: "20px" },
  ".cm-gutter-lint": { width: "16px" },
  ".cm-gutter-lint .cm-gutterElement": { padding: "0", display: "flex", alignItems: "center", justifyContent: "center" },
  ".cm-lint-marker": { width: "12px", height: "12px" },
  ".cm-lintRange-error": { backgroundImage: "none", textDecoration: "underline wavy #ff5050",
    textDecorationSkipInk: "none", textUnderlineOffset: "1px" },
  ".cm-tooltip": { backgroundColor: "#ffffff", color: "#000000", border: "1px solid #767676",
    boxShadow: "2px 2px 4px rgba(0, 0, 0, .3)", fontFamily: "var(--ui-font)", fontSize: "12px" },
  // The completion list, in the code's font. @codemirror/autocomplete gives
  // it a monospace font and line height with these selectors, so only a rule
  // as specific as its own replaces them.
  ".cm-tooltip.cm-tooltip-autocomplete > ul": { fontFamily: "var(--code-font)", fontSize: "var(--code-size)" },
  ".cm-tooltip.cm-tooltip-autocomplete > ul > li": { lineHeight: "var(--code-line)" },
  ".cm-tooltip-autocomplete ul li[aria-selected]": { backgroundColor: "var(--editor-selection)", color: "#ffffff" },
  ".cm-completionDetail": { fontStyle: "normal", opacity: ".7" },
  ".cm-diagnostic-error": { borderLeft: "5px solid #f03030" },
  ".b3d-signature": { padding: "3px 6px", fontFamily: "var(--code-font)", fontSize: "var(--code-size)",
    lineHeight: "var(--code-line)", whiteSpace: "pre-wrap" },
  ".b3d-signature b": { color: "#0050a0" },
  ".b3d-signature .optional": { color: "#6d6d6d" },
  ".cm-breakpoint-gutter": { width: "16px", cursor: "pointer", backgroundColor: "var(--editor-gutter)" },
  ".cm-breakpoint-gutter .cm-gutterElement": { display: "flex", alignItems: "center", justifyContent: "center" },
  // The desktop's marker: a red circle with a dark red edge.
  ".b3d-breakpoint": { width: "11px", height: "11px", boxSizing: "border-box", borderRadius: "50%",
    backgroundColor: "var(--breakpoint)", border: "1px solid var(--breakpoint-edge)" },
  ".cm-breakpoint-gutter .cm-gutterElement:hover:empty::after": { content: '""', width: "11px", height: "11px",
    borderRadius: "50%", backgroundColor: "rgba(240, 48, 48, .45)" },
  ".b3d-stopped-line": { backgroundColor: "var(--editor-stopped)" },
  // Lines Blip changed: the comment colour's gold, faintly, with an edge.
  ".b3d-blip-changed": { backgroundColor: "rgba(255, 238, 0, .13)", boxShadow: "inset 3px 0 0 rgba(255, 238, 0, .75)" },
  ".cm-panels": { backgroundColor: "var(--chrome)", color: "#000000", fontFamily: "var(--ui-font)", fontSize: "12px" },
  ".cm-panels.cm-panels-bottom": { borderTop: "1px solid var(--chrome-border)" },
  ".cm-panels.cm-panels-top": { borderBottom: "1px solid #a0a0a0" },
  ".cm-textfield": { backgroundColor: "#ffffff", color: "#000000", border: "1px solid var(--control-border)",
    borderRadius: "3px" },
  ".cm-button": { backgroundImage: "none", backgroundColor: "var(--control)", color: "#000000",
    border: "1px solid var(--control-border)", borderRadius: "3px" },
  // The find bar, with Windows' fields and buttons, as the page's own.
  ".b3d-find": { position: "relative", display: "flex", flexDirection: "column", gap: "4px", padding: "5px 36px 5px 6px" },
  ".b3d-find-row": { display: "flex", flexWrap: "wrap", alignItems: "center", gap: "4px 6px" },
  // The fields narrow with the editor, alike, to leave the first row room
  // for the rest of it.
  ".b3d-find input[type=text]": { flex: "none", width: "clamp(80px, calc(100% - 26em), 220px)", height: "22px",
    padding: "0 4px", border: "1px solid var(--field-border)", borderRadius: "0", backgroundColor: "#ffffff",
    color: "#000000", font: "12px var(--ui-font)" },
  ".b3d-find input[type=text]:focus": { borderColor: "var(--accent)", outline: "none" },
  ".b3d-find button": { height: "22px", padding: "0 10px", border: "1px solid var(--control-border)", borderRadius: "0",
    backgroundColor: "var(--control)", color: "#000000", font: "12px var(--ui-font)", cursor: "default" },
  ".b3d-find button:hover": { backgroundColor: "#e5f1fb", borderColor: "var(--accent)" },
  ".b3d-find button:active": { backgroundColor: "#cce4f7", borderColor: "#005499" },
  ".b3d-find .b3d-find-step": { width: "24px", padding: "0 0 2px", fontSize: "17px", lineHeight: "17px" },
  ".b3d-find label": { display: "flex", alignItems: "center", gap: "4px", whiteSpace: "nowrap" },
  ".b3d-find input[type=checkbox]": { margin: "0" },
  ".b3d-find-count": { color: "var(--muted)", whiteSpace: "nowrap" },
  ".b3d-find .b3d-find-close": { position: "absolute", top: "5px", right: "6px", width: "24px", padding: "0",
    borderColor: "transparent", backgroundColor: "transparent", fontSize: "16px", lineHeight: "18px" },
  ".b3d-find .b3d-find-close:hover": { backgroundColor: "#e81123", borderColor: "#e81123", color: "#ffffff" },
  ".cm-searchMatch": { backgroundColor: "rgba(255, 238, 0, .3)", outline: "1px solid rgba(255, 238, 0, .7)" },
  ".cm-searchMatch.cm-searchMatch-selected": { backgroundColor: "rgba(255, 238, 0, .55)" },
  // A number that changed in the running program (Live), for a moment.
  ".b3d-live-number": { backgroundColor: "rgba(255, 238, 0, .35)", borderRadius: "2px",
    transition: "background-color .6s ease-out" },
}, { dark: true });

// Breakpoints: a marker on each line that has one, moving with the text.
const breakpointMarker = new (class extends GutterMarker {
  toDOM() {
    const dot = document.createElement("div");
    dot.className = "b3d-breakpoint";
    dot.title = "Breakpoint: the program stops before this line runs. Click to remove.";
    return dot;
  }
})();
const toggleBreakpoint = StateEffect.define({ map: (pos, mapping) => mapping.mapPos(pos) });
const setBreakpoints = StateEffect.define();
const breakpointField = StateField.define({
  create: () => RangeSet.empty,
  update(set, transaction) {
    set = set.map(transaction.changes);
    for (const effect of transaction.effects) {
      if (effect.is(setBreakpoints)) {
        set = RangeSet.of(effect.value.map((from) => breakpointMarker.range(from)), true);
      } else if (effect.is(toggleBreakpoint)) {
        let found = false;
        set.between(effect.value, effect.value, () => { found = true; });
        set = found ? set.update({ filter: (from) => from !== effect.value }) :
          set.update({ add: [breakpointMarker.range(effect.value)] });
      }
    }
    return set;
  },
});
// The rows, from 1, of a document's breakpoints.
const breakpointRows = (state) => {
  const rows = new Set();
  for (const cursor = state.field(breakpointField).iter(); cursor.value; cursor.next()) {
    rows.add(state.doc.lineAt(cursor.from).number);
  }
  return [...rows].sort((a, b) => a - b);
};

// The line the debugger stopped at.
const setStoppedLine = StateEffect.define();
const stoppedLine = Decoration.line({ class: "b3d-stopped-line" });
const stoppedField = StateField.define({
  create: () => Decoration.none,
  update(set, transaction) {
    set = set.map(transaction.changes);
    for (const effect of transaction.effects) {
      if (!effect.is(setStoppedLine)) continue;
      const row = effect.value;
      set = row && row <= transaction.state.doc.lines ?
        Decoration.set([stoppedLine.range(transaction.state.doc.line(row).from)]) : Decoration.none;
    }
    return set;
  },
  provide: (field) => EditorView.decorations.from(field),
});

// The lines Blip changed, marked until his next request or until cleared:
// their starts, as a set of line decorations.
const setChangeMarks = StateEffect.define();
const changedLine = Decoration.line({ class: "b3d-blip-changed" });
const changeMarkField = StateField.define({
  create: () => Decoration.none,
  update(set, transaction) {
    set = set.map(transaction.changes);
    for (const effect of transaction.effects) {
      if (!effect.is(setChangeMarks)) continue;
      const doc = transaction.state.doc;
      const starts = [...new Set(effect.value.filter((row) => row >= 1 && row <= doc.lines).map((row) => doc.line(row).from))];
      set = Decoration.set(starts.sort((a, b) => a - b).map((from) => changedLine.range(from)));
    }
    return set;
  },
  provide: (field) => EditorView.decorations.from(field),
});

// Numbers that just changed in the running program (Live), marked briefly.
const setLiveNumbers = StateEffect.define();
const liveNumber = Decoration.mark({ class: "b3d-live-number" });
const liveNumberField = StateField.define({
  create: () => Decoration.none,
  update(set, transaction) {
    set = set.map(transaction.changes);
    for (const effect of transaction.effects) {
      if (!effect.is(setLiveNumbers)) continue;
      const length = transaction.state.doc.length;
      set = Decoration.set(effect.value.filter(([from, to]) => from < to && to <= length)
        .map(([from, to]) => liveNumber.range(from, to)), true);
    }
    return set;
  },
  provide: (field) => EditorView.decorations.from(field),
});

// The call that the cursor is in the arguments of, if any: the innermost
// open bracket after a command's name, or else a command that starts the
// statement, as in Text 0,0,"Hello". Returns { command, argument }.
function callAt(text, commands) {
  const frames = [{ name: null, commas: 0 }];
  let statementCommand = null, statementCommas = 0, previousWord = null, statementStart = true;
  for (let k = 0; k < text.length; ) {
    const ch = text[k];
    if (ch === '"') {
      const end = text.indexOf('"', k + 1);
      if (end < 0) break;
      k = end + 1;
      previousWord = null;
      continue;
    }
    if (ch === ";") break;
    if (/[A-Za-z_]/.test(ch)) {
      const word = /^[A-Za-z_]\w*[%#$&@]?/.exec(text.slice(k))[0];
      const bare = word.replace(/[%#$&@]$/, "").toLowerCase();
      if (statementStart && frames.length === 1) {
        statementCommand = commands.get(bare) || null;
        statementCommas = 0;
      }
      statementStart = bare === "then" || bare === "else";
      previousWord = bare;
      k += word.length;
      continue;
    }
    if (ch === "(") {
      frames.push({ name: previousWord, commas: 0 });
    } else if (ch === ")") {
      if (frames.length > 1) frames.pop();
    } else if (ch === ",") {
      if (frames.length > 1) frames[frames.length - 1].commas++;
      else statementCommas++;
    } else if (ch === ":") {
      frames.length = 1;
      statementCommand = null;
      statementStart = true;
    }
    if (!/\s/.test(ch)) {
      previousWord = null;
      if (ch !== ":") statementStart = false;
    }
    ++k;
  }
  for (let k = frames.length - 1; k > 0; --k) {
    const command = frames[k].name && commands.get(frames[k].name);
    if (command) return { command, argument: frames[k].commas };
  }
  return statementCommand ? { command: statementCommand, argument: statementCommas } : null;
}

function signatureElement(command, argument) {
  const element = document.createElement("div");
  element.className = "b3d-signature";
  element.append(command.name + (command.returns || "") + " ");
  command.params.forEach((param, index) => {
    if (index) element.append(", ");
    const text = `${param.name}${param.sigil}${param.default === undefined ? "" : "=" + param.default}`;
    const part = document.createElement(index === argument ? "b" : "span");
    if (param.default !== undefined && index !== argument) part.className = "optional";
    part.textContent = text;
    element.append(part);
  });
  if (!command.params.length) element.append("(no parameters)");
  return element;
}

// The line commands, as the desktop IDEs have them (Edit > Comment or
// Uncomment Lines, Indent Lines and Outdent Lines): each changes the lines
// the selection touches, as one step to undo, and selects them. A selection
// that ends at the start of a line leaves that line out.
function editLines(view, transform) {
  const { state } = view;
  const range = state.selection.main;
  const first = state.doc.lineAt(range.from).number;
  let last = state.doc.lineAt(range.to).number;
  if (last > first && !range.empty && state.doc.line(last).from === range.to) --last;
  const from = state.doc.line(first).from, to = state.doc.line(last).to;
  const lines = [];
  for (let number = first; number <= last; ++number) lines.push(state.doc.line(number).text);
  const text = transform(lines).join(state.lineBreak);
  view.dispatch({ changes: { from, to, insert: text }, selection: { anchor: from, head: from + text.length },
    scrollIntoView: true, userEvent: "input" });
  return true;
}
// Uncomments when every line with text is a comment; otherwise comments
// each line with text.
const commentLines = (view) => editLines(view, (lines) => {
  const commented = lines.every((line) => !line.trim() || line.trimStart().startsWith(";"));
  return lines.map((line) => !commented ? (line ? ";" + line : line) : line.replace(/^([ \t]*);/, "$1"));
});
const indentLines = (view) => editLines(view, (lines) => lines.map((line) => line ? "\t" + line : line));
const outdentLines = (view) => editLines(view, (lines) => lines.map((line) =>
  line.startsWith("\t") ? line.slice(1) : line.replace(/^ {1,4}/, "")));
const LINE_COMMANDS = { goToLine: gotoLine, comment: commentLines, indent: indentLines, outdent: outdentLines };

// The find bar (Edit > Find… and Replace…), across the top of the editor as
// in the Windows IDE. It finds as Find in Files does (findfiles.js): the text
// as typed, not one that only normalising its characters would make, with or
// without its case, and with Whole word, no letter, digit or _ either side.
// CodeMirror's own whole words have other letters, so a query's test says
// which rule it has.
function asTyped(match, state) {
  const { search, caseSensitive } = getSearchQuery(state);
  return caseSensitive ? match === search : match.toLowerCase() === search.toLowerCase();
}
function asTypedWord(match, state, from, to) {
  return asTyped(match, state) && !(from > 0 && WORD.test(state.sliceDoc(from - 1, from))) &&
    !(to < state.doc.length && WORD.test(state.sliceDoc(to, to + 1)));
}
const findQuery = ({ search, replace = "", caseSensitive = false, wholeWord = false }) =>
  new SearchQuery({ search, replace, caseSensitive, literal: true, test: wholeWord ? asTypedWord : asTyped });
const isWholeWord = (query) => query.test === asTypedWord;
// The first match at or after a position, or else the first of all; the
// last before one, or else the last of all.
const matchFrom = (matches, position) => matches.find((match) => match.from >= position) ?? matches[0];
const matchBefore = (matches, position) => matches.findLast((match) => match.from < position) ?? matches.at(-1);

// onDebugKey(key) takes the debugger's keys: "F10", "F11" and "Shift-F11".
// onBreakpoints(path, rows) hears of each change to a file's breakpoints, and
// onCursor(row, column) of each move of the cursor, both from 1. Ctrl+B
// calls onWide, which switches the full-width layout. onLiveEdit() hears of
// a number dragged or a colour picked (livetools.js), after onChange.
// onStatus(text) says what the find bar's Replace all did, in the status bar.
export function createEditor({ parent, commands, onChange, onRun, onHelp, isExtended, onDebugKey = () => false,
  onBreakpoints = () => {}, onCursor = () => {}, onWide = () => false, onLiveEdit = () => {}, onBlip = () => false,
  onStatus = () => {}, lineNumbers: numbered = true }) {
  const available = (command) => !command.extended || isExtended();

  const complete = (context) => {
    const word = context.matchBefore(/[A-Za-z_]\w*/);
    if (!word || (word.from === word.to && !context.explicit)) return null;
    const line = context.state.doc.lineAt(context.pos);
    const before = line.text.slice(0, context.pos - line.from);
    // Not inside a comment or a string.
    if (/;/.test(before.replace(/"[^"]*"/g, "")) || (before.split('"').length - 1) % 2) return null;
    const options = [];
    for (const command of commands.values()) {
      if (!available(command)) continue;
      options.push({
        label: command.name,
        type: "function",
        detail: command.returns ? typeName(command.returns) : "",
        info: () => signatureElement(command, -1),
      });
    }
    for (const [lower, word] of KEYWORDS) {
      if (commands.has(lower) || (EXTENDED_ONLY.has(lower) && !isExtended())) continue;
      const constant = CONSTANTS.get(lower);
      options.push(constant ? { label: word, type: "constant", detail: `= ${constant.value}` } : { label: word, type: "keyword" });
    }
    return { from: word.from, options, validFor: /^\w*$/ };
  };

  // The parameter hint follows the cursor: above its line, or below it at
  // the top of the editor, where there is no room above. It stays for as
  // long as the cursor is in a call, so it keeps inside the editor's text:
  // over the splitter or the program beside it, it would take their clicks.
  const signature = StateField.define({
    create: () => null,
    update(value, transaction) {
      if (!transaction.docChanged && !transaction.selection) return value;
      const state = transaction.state;
      const range = state.selection.main;
      if (!range.empty) return null;
      const line = state.doc.lineAt(range.head);
      const call = callAt(line.text.slice(0, range.head - line.from), commands);
      if (!call || !available(call.command)) return null;
      return {
        pos: range.head,
        above: true,
        arrow: false,
        create: (view) => {
          const dom = signatureElement(call.command, call.argument);
          // The hint's anchor: the cursor, but reaching left as far as keeps
          // the hint inside the text, which a long one wraps to fit
          // (CodeMirror measures the hint after this). With the cursor out
          // of sight, it is the cursor's own, so that the hint hides.
          const getCoords = (pos) => {
            const at = view.coordsAtPos(pos), text = textArea(view);
            if (!at || at.right < text.left || at.left > text.right) return at;
            const left = text.left + TOOLTIP_INSET, right = text.right - TOOLTIP_INSET;
            dom.style.maxWidth = `${Math.max(0, right - left)}px`;
            const start = Math.max(left, Math.min(at.left, right - dom.getBoundingClientRect().width));
            return { left: start, right: at.right, top: at.top, bottom: at.bottom };
          };
          return { dom, getCoords };
        },
      };
    },
    provide: (field) => showTooltip.from(field),
  });

  // The word at the cursor, for help, or else the nearest one before it on
  // the line, as in CreateCube(|).
  const wordAtCursor = (state) => {
    const range = state.selection.main;
    const line = state.doc.lineAt(range.head);
    const offset = range.head - line.from;
    const before = /\w*$/.exec(line.text.slice(0, offset))[0];
    const after = /^\w*/.exec(line.text.slice(offset))[0];
    if (before + after) return before + after;
    const earlier = /(\w+)\W*$/.exec(line.text.slice(0, offset));
    return earlier ? earlier[1] : "";
  };

  // The language is in a compartment, so that a new mode colours the code
  // again: Extended's number suffixes are numbers only in Extended.
  let blitz = blitzLanguage(commands, isExtended);
  const language = new Compartment();
  let current = null;                  // the path of the file shown
  const states = new Map();            // path -> EditorState
  const scrolls = new Map();           // path -> its scroll snapshot, as the file was last shown
  // A buddy session's files (buddies/session.js), while one is open: textOf(path)
  // is a shared file's text or null, local(path, changes) passes on changes made
  // here, and selection(path, range) the cursor's moves.
  let sharing = null;
  // Watchers of a buddy session read, and do not type.
  const editable = new Compartment();
  const toggleAt = (view, pos) => {
    view.dispatch({ effects: toggleBreakpoint.of(view.state.doc.lineAt(pos).from) });
    return true;
  };
  // Changes when the code font does, so that the editor measures it again.
  const font = new Compartment();
  const breakpoints = [
    breakpointField,
    gutter({
      class: "cm-breakpoint-gutter",
      markers: (view) => view.state.field(breakpointField),
      initialSpacer: () => breakpointMarker,
      domEventHandlers: {
        mousedown: (view, line) => toggleAt(view, line.from),
      },
    }),
  ];
  // The line numbers, which View > Line numbers shows or hides; a double
  // click on one sets a breakpoint, as on the breakpoint gutter.
  const numbers = new Compartment();
  const numberGutter = lineNumbers({ domEventHandlers: {
    mousedown: (view, line, event) => event.detail === 2 && toggleAt(view, line.from) } });

  // The find bar is CodeMirror's search panel, which each file's document
  // opens or closes for itself; show() keeps it as it was, with its text and
  // options, as another file is shown. finder holds what the files share:
  // whether the bar shows its Replace row, the bar while it is open, and
  // where the selection was as it opened in the file shown, which finding as
  // you type starts from.
  const finder = { replacing: false, bar: null, origin: null };
  // The matches in a document, [{ from, to }], kept while neither it nor the
  // query changes.
  let found = { doc: null, query: null, matches: [] };
  const matchesOf = (state) => {
    const query = getSearchQuery(state);
    if (found.doc !== state.doc || found.query !== query) {
      const matches = [];
      if (query.valid) {
        for (const cursor = query.getCursor(state); !cursor.next().done;) {
          matches.push({ from: cursor.value.from, to: cursor.value.to });
        }
      }
      found = { doc: state.doc, query, matches };
    }
    return found.matches;
  };
  const isMatch = (state, { from, to }) => matchesOf(state).some((match) => match.from === from && match.to === to);
  // Selects a match, scrolling to it unless it is in sight.
  const selectMatch = (view, { from, to }) => {
    const inSight = view.visibleRanges.some((range) => from >= range.from && to <= range.to);
    view.dispatch({ selection: { anchor: from, head: to }, userEvent: "select.search",
      effects: EditorView.scrollIntoView(EditorSelection.range(from, to), { y: inSight ? "nearest" : "center" }) });
  };
  // With no text to find yet, the Find field, in the find bar, which opens
  // if it was closed.
  const noText = (view) => {
    if (finder.bar) finder.bar.show(finder.replacing, "find");
    else openFinder(view, false);
    return true;
  };
  // Enter, › and F3: the next match after the selection; Shift+Enter, ‹ and
  // Shift+F3: the one before it, each wrapping round.
  const step = (view, backwards) => {
    if (!getSearchQuery(view.state).valid) return noText(view);
    const matches = matchesOf(view.state), range = view.state.selection.main;
    const match = backwards ? matchBefore(matches, range.from) : matchFrom(matches, range.to);
    if (match) selectMatch(view, match);
    return true;
  };
  // Finding as you type: the first match at or after where the selection
  // started as the bar opened, wrapping to the top; with none, the selection
  // goes back to where it was.
  const seek = (view) => {
    const origin = finder.origin || { anchor: 0, head: 0 }, { ranges, main } = view.state.selection;
    const match = matchFrom(matchesOf(view.state), Math.min(origin.anchor, origin.head));
    if (match) selectMatch(view, match);
    else if (ranges.length > 1 || main.anchor !== origin.anchor || main.head !== origin.head) {
      view.dispatch({ selection: origin, scrollIntoView: true, userEvent: "select.search" });
    }
  };
  // Edit > Find… shows the bar's first row, and Replace… its Replace row
  // too. As the bar opens, a selection on one line becomes the text to find,
  // and the Find field has the focus, or for Replace… the Replace field once
  // there is text to find; when the bar is open already, they only move the
  // focus.
  const openFinder = (view, replacing) => {
    finder.replacing = replacing;
    let field = replacing ? "replace" : "find";
    if (!searchPanelOpen(view.state)) {
      let query = getSearchQuery(view.state);
      const range = view.state.selection.main, selected = view.state.sliceDoc(range.from, range.to);
      if (selected && !selected.includes("\n")) {
        query = findQuery({ search: selected, replace: query.replace, caseSensitive: query.caseSensitive,
          wholeWord: isWholeWord(query) });
      }
      finder.origin = { anchor: range.anchor, head: range.head };
      openSearchPanel(view);
      // Opening chose a query of its own.
      view.dispatch({ effects: setSearchQuery.of(query) });
      if (!query.search) field = "find";
    }
    if (finder.bar) finder.bar.show(replacing, field);
    return true;
  };
  // Replace: the selection, when it is a match, and then the next match.
  const replaceMatch = (view) => {
    const query = getSearchQuery(view.state);
    if (!query.valid) return noText(view);
    const { from, to } = view.state.selection.main;
    if (isMatch(view.state, { from, to })) {
      view.dispatch({ changes: { from, to, insert: query.replace }, selection: { anchor: from + query.replace.length },
        userEvent: "input.replace" });
    }
    return step(view, false);
  };
  // Replace all: every match in the file, as one step to undo.
  const replaceEvery = (view) => {
    const query = getSearchQuery(view.state), matches = query.valid ? matchesOf(view.state) : [];
    if (!matches.length) return true;
    view.dispatch({ changes: matches.map(({ from, to }) => ({ from, to, insert: query.replace })),
      userEvent: "input.replace.all", annotations: isolateHistory.of("full") });
    onStatus(`Replaced ${counted(matches.length, "match", "matches")}`);
    return true;
  };
  // The bar: Find field, ‹, ›, Match case, Whole word, the count and ×; and
  // under them the Replace field, Replace and Replace all, for Replace….
  const createFindBar = (view) => {
    const element = (tag, attributes = {}, ...children) => {
      const node = document.createElement(tag);
      for (const [name, value] of Object.entries(attributes)) node.setAttribute(name, value);
      node.append(...children);
      return node;
    };
    const findField = element("input", { type: "text", class: "b3d-find-text", placeholder: "Find", "aria-label": "Find",
      spellcheck: "false", "main-field": "true" });
    const previous = element("button", { type: "button", class: "b3d-find-step b3d-find-previous",
      title: "Previous match (Shift+F3)", "aria-label": "Previous match" }, "‹");
    const next = element("button", { type: "button", class: "b3d-find-step b3d-find-next", title: "Next match (F3)",
      "aria-label": "Next match" }, "›");
    const matchCase = element("input", { type: "checkbox", class: "b3d-match-case" });
    const wholeWord = element("input", { type: "checkbox", class: "b3d-whole-word" });
    const count = element("span", { class: "b3d-find-count", role: "status" });
    const close = element("button", { type: "button", class: "b3d-find-close", title: "Close (Esc)", "aria-label": "Close" },
      "×");
    const replaceField = element("input", { type: "text", class: "b3d-replace-text", placeholder: "Replace with",
      "aria-label": "Replace with", spellcheck: "false" });
    const replace = element("button", { type: "button", class: "b3d-replace",
      title: "Replace this match and find the next" }, "Replace");
    const replaceAll = element("button", { type: "button", class: "b3d-replace-all",
      title: "Replace every match in this file" }, "Replace all");
    const replaceRow = element("div", { class: "b3d-find-row b3d-replace-row" }, replaceField, replace, replaceAll);
    const dom = element("div", { class: "b3d-find", role: "search", "aria-label": "Find in this file" },
      element("div", { class: "b3d-find-row" }, findField, previous, next,
        element("label", {}, matchCase, "Match case"), element("label", {}, wholeWord, "Whole word"), count, close),
      replaceRow);
    // The fields as the query is: the same in every file.
    let shown = null;
    const fill = (query) => {
      shown = query;
      findField.value = query.search;
      replaceField.value = query.replace;
      matchCase.checked = query.caseSensitive;
      wholeWord.checked = isWholeWord(query);
    };
    // "3 of 12" when the selection is a match, else "12 matches".
    const counter = () => {
      const matches = matchesOf(view.state), { from, to } = view.state.selection.main;
      const at = matches.findIndex((match) => match.from === from && match.to === to);
      count.textContent = !findField.value ? "" : !matches.length ? "No matches" :
        at >= 0 ? `${(at + 1).toLocaleString("en-US")} of ${matches.length.toLocaleString("en-US")}` :
        counted(matches.length, "match", "matches");
    };
    // Each change to the Find field, Match case or Whole word finds again.
    const commit = (seeking) => {
      const query = findQuery({ search: findField.value, replace: replaceField.value, caseSensitive: matchCase.checked,
        wholeWord: wholeWord.checked });
      if (!query.eq(getSearchQuery(view.state))) {
        shown = query;
        view.dispatch({ effects: setSearchQuery.of(query) });
      }
      if (seeking) seek(view);
    };
    findField.addEventListener("input", () => commit(true));
    matchCase.addEventListener("change", () => commit(true));
    wholeWord.addEventListener("change", () => commit(true));
    replaceField.addEventListener("input", () => commit(false));
    previous.addEventListener("click", () => step(view, true));
    next.addEventListener("click", () => step(view, false));
    replace.addEventListener("click", () => replaceMatch(view));
    replaceAll.addEventListener("click", () => replaceEvery(view));
    close.addEventListener("click", () => {
      closeSearchPanel(view);
      view.focus();
    });
    // Esc, F3 and the find keys anywhere in the bar; Enter in the Find field
    // finds, and in the Replace field replaces.
    dom.addEventListener("keydown", (event) => {
      if (runScopeHandlers(view, event, "search-panel")) {
        event.preventDefault();
      } else if (event.key === "Enter" && (event.target === findField || event.target === replaceField)) {
        event.preventDefault();
        if (event.target === replaceField) replaceMatch(view);
        else step(view, event.shiftKey);
      }
    });
    const bar = {
      dom,
      top: true,
      mount: counter,
      update(update) {
        for (const transaction of update.transactions) {
          for (const effect of transaction.effects) {
            if (effect.is(setSearchQuery) && effect.value !== shown) fill(effect.value);
          }
        }
        const { origin } = finder;
        if (update.docChanged && origin) {
          finder.origin = { anchor: update.changes.mapPos(origin.anchor), head: update.changes.mapPos(origin.head) };
        }
        counter();
      },
      destroy() {
        if (finder.bar === bar) finder.bar = null;
      },
      // The Replace row shown or not, and the focus in a field, its text selected.
      show(replacing, field) {
        replaceRow.hidden = !replacing;
        const focused = field === "replace" ? replaceField : findField;
        focused.focus();
        focused.select();
      },
    };
    fill(getSearchQuery(view.state));
    replaceRow.hidden = !finder.replacing;
    finder.bar = bar;
    return bar;
  };

  const common = [
    breakpoints, stoppedField, liveNumberField, changeMarkField, cursorField, editable.of([]),
    numbers.of(numbered ? numberGutter : []), highlightSpecialChars(), history(), drawSelection(),
    EditorState.allowMultipleSelections.of(true), indentOnInput(), bracketMatching(), closeBrackets(),
    rectangularSelection(), highlightSelectionMatches(), lintGutter(), tooltips({ tooltipSpace }),
    search({ top: true, literal: true, createPanel: createFindBar }),
    syntaxHighlighting(highlight), theme, font.of([]), indentUnit.of("\t"), EditorState.tabSize.of(4),
    keymap.of([
      { key: "F5", run: () => { onRun(); return true; } },
      { key: "Mod-Enter", run: () => { onRun(); return true; } },
      { key: "F1", run: (view) => { onHelp(wordAtCursor(view.state)); return true; } },
      { key: "F9", run: (view) => toggleAt(view, view.state.selection.main.head) },
      { key: "F10", run: () => onDebugKey("F10") },
      { key: "F11", run: () => onDebugKey("F11") },
      { key: "Shift-F11", run: () => onDebugKey("Shift-F11") },
      { key: "Mod-b", run: () => onWide(), preventDefault: true },
      // Blip's panel, before CodeMirror's own Mod-i (select the syntax around).
      { key: "Mod-i", run: () => onBlip() },
      // Go to Line, comment and indent, on the Windows IDE's keys.
      { key: "Mod-g", run: gotoLine, preventDefault: true },
      { key: "Mod-/", run: commentLines },
      { key: "Mod-]", run: indentLines },
      { key: "Mod-[", run: outdentLines },
      // The find bar's keys, as the Windows IDE's, in the editor and in the
      // bar. Esc closes the bar before it shrinks the selection. Ctrl+R is
      // never the browser's reload.
      { key: "Mod-f", run: (view) => openFinder(view, false), scope: "editor search-panel", preventDefault: true },
      { key: "Mod-r", run: (view) => openFinder(view, true), scope: "editor search-panel", preventDefault: true },
      { key: "F3", run: (view) => step(view, false), shift: (view) => step(view, true), scope: "editor search-panel",
        preventDefault: true },
      { key: "Escape", run: closeSearchPanel, scope: "editor search-panel" },
      // CodeMirror's: the next match of the selection added to it, and every one.
      { key: "Mod-d", run: selectNextOccurrence, preventDefault: true },
      { key: "Mod-Shift-l", run: selectSelectionMatches },
      ...closeBracketsKeymap, ...defaultKeymap, ...historyKeymap, ...completionKeymap,
      indentWithTab,
    ]),
    EditorView.updateListener.of((update) => {
      if (sharing && current) {
        for (const transaction of update.transactions) {
          if (transaction.docChanged && !transaction.annotation(fromRoom)) sharing.local(current, transaction.changes);
        }
        if (update.docChanged || update.selectionSet) sharing.selection(current, update.state.selection.main);
      }
      // Error marks move with the text until the next build replaces them.
      if (update.docChanged) onChange(current, update.state.doc.toString());
      if (update.docChanged || update.selectionSet) reportCursor(update.state);
      if (update.docChanged || update.transactions.some((t) => t.effects.some((e) => e.is(toggleBreakpoint)))) {
        const before = update.startState.field(breakpointField), after = update.state.field(breakpointField);
        if (before !== after && current) onBreakpoints(current, breakpointRows(update.state));
      }
    }),
  ];
  // The language as the mode is now, for each file opened.
  const sourceExtensions = () => [language.of(blitz), keywordCase(commands, isExtended), autocompletion({ override: [complete], activateOnTyping: true, icons: false }),
    signature, liveTools({ commands, onLiveEdit })];
  const createState = (path, text) => {
    // A shared file opens as the session has it, which may be newer than the
    // project's copy.
    const shared = sharing && sharing.textOf(path);
    const state = EditorState.create({
      doc: typeof shared === "string" ? shared : text,
      extensions: [common, /.bb$/i.test(path) ? sourceExtensions() : []],
    });
    return readOnly ? state.update({ effects: editable.reconfigure(EditorState.readOnly.of(true)) }).state : state;
  };
  let readOnly = false;
  const view = new EditorView({ parent, state: createState("untitled.bb", "") });
  // The cursor's row and column, as the desktop IDE's status bar gives them.
  function reportCursor(state) {
    const head = state.selection.main.head, line = state.doc.lineAt(head);
    onCursor(line.number, head - line.from + 1);
  }
  // Shows a file's document. The find bar stays open or closed, with its
  // text and options, and finding as you type starts where this file's
  // selection is.
  const show = (state) => {
    const query = getSearchQuery(view.state), open = searchPanelOpen(view.state);
    view.setState(state);
    const { anchor, head } = view.state.selection.main;
    finder.origin = { anchor, head };
    if (open && !searchPanelOpen(view.state)) openSearchPanel(view);
    else if (!open && searchPanelOpen(view.state)) closeSearchPanel(view);
    if (!getSearchQuery(view.state).eq(query)) view.dispatch({ effects: setSearchQuery.of(query) });
    reportCursor(view.state);
  };

  // Marks errors in a document: [{ row, col, message, severity }], rows and
  // columns from 1.
  const diagnosticsFor = (state, errors) => {
    const doc = state.doc;
    return errors.filter((error) => error.row >= 1 && error.row <= doc.lines).map((error) => {
      const line = doc.line(error.row);
      const from = Math.min(line.from + Math.max(0, (error.col || 1) - 1), line.to);
      // The word at the column, or else the rest of the line.
      const word = /^\w+/.exec(line.text.slice(from - line.from));
      let to = word ? from + word[0].length : line.to;
      if (to === from) to = Math.min(from + 1, line.to);
      return { from: to > from ? from : line.from, to: to > from ? to : line.to,
        severity: error.severity || "error", message: error.message };
    });
  };
  const stateOf = (path) => path === current ? view.state : states.get(path);

  return {
    view,
    get path() { return current; },
    get text() { return view.state.doc.toString(); },
    // The text of every file opened, which may be newer than the project's.
    texts() {
      if (current) states.set(current, view.state);
      return new Map([...states].map(([path, state]) => [path, state.doc.toString()]));
    },
    isOpen: (path) => states.has(path) || path === current,
    // Shows a file, with the text given the first time it is opened.
    open(path, text) {
      if (current === path) return;
      if (current) {
        states.set(current, view.state);
        scrolls.set(current, view.scrollSnapshot());
      }
      if (!states.has(path)) states.set(path, createState(path, text));
      current = path;
      show(states.get(path));
      // Each file opens where it was left, or at its start.
      view.dispatch({ effects: scrolls.get(path) || EditorView.scrollIntoView(0, { y: "start", x: "start" }) });
    },
    // Opens a file without showing it, so it can hold error marks.
    ensure(path, text) {
      if (path !== current && !states.has(path)) states.set(path, createState(path, text));
    },
    // Replaces a file's text, as opening another version of it does.
    setText(text) {
      view.dispatch({ changes: { from: 0, to: view.state.doc.length, insert: text }, selection: { anchor: 0 },
        scrollIntoView: true });
    },
    close(path) {
      states.delete(path);
      scrolls.delete(path);
      if (current === path) {
        current = null;
        show(createState("untitled.bb", ""));
      }
    },
    rename(from, to) {
      const state = stateOf(from);
      if (!state) return;
      states.delete(from);
      // The breakpoints move with the file.
      const rows = breakpointRows(state);
      const created = createState(to, state.doc.toString());
      const renamed = created.update({ effects: setBreakpoints.of(rows.map((row) => created.doc.line(row).from)) }).state;
      states.set(to, renamed);
      if (current === from) {
        current = to;
        show(renamed);
      }
    },
    closeAll() {
      states.clear();
      scrolls.clear();
      current = null;
      show(createState("untitled.bb", ""));
    },
    // Marks errors, by file: Map path -> [{ row, col, message }]. Files not
    // named lose their marks.
    showErrors(byPath) {
      if (current) states.set(current, view.state);
      for (const [path, state] of states) {
        const diagnostics = diagnosticsFor(state, byPath.get(path) || []);
        if (path === current) view.dispatch(setDiagnostics(view.state, diagnostics));
        else states.set(path, state.update(setDiagnostics(state, diagnostics)).state);
      }
    },
    // Breakpoints, by file: Map path -> rows, from 1.
    breakpoints() {
      if (current) states.set(current, view.state);
      const all = new Map();
      for (const [path, state] of states) {
        const rows = breakpointRows(state);
        if (rows.length) all.set(path, rows);
      }
      return all;
    },
    // Program > Toggle breakpoint, as F9: on the cursor's line.
    toggleBreakpoint() {
      toggleAt(view, view.state.selection.main.head);
      view.focus();
    },
    // Program > Remove all breakpoints: in every file.
    clearBreakpoints() {
      if (current) states.set(current, view.state);
      const effects = setBreakpoints.of([]);
      for (const [path, state] of states) {
        if (!breakpointRows(state).length) continue;
        if (path === current) view.dispatch({ effects });
        else states.set(path, state.update({ effects }).state);
      }
    },
    // The command or name at the cursor, as F1 reads it.
    wordAtCursor() {
      return wordAtCursor(view.state);
    },
    // Sets a file's breakpoints, opening it without showing it.
    setBreakpoints(path, text, rows) {
      this.ensure(path, text);
      const state = stateOf(path);
      const positions = rows.filter((row) => row >= 1 && row <= state.doc.lines).map((row) => state.doc.line(row).from);
      const effects = setBreakpoints.of(positions);
      if (path === current) view.dispatch({ effects });
      else states.set(path, state.update({ effects }).state);
    },
    // Marks the line the debugger stopped at, in the file shown; or, with no
    // row, no line in any file.
    showStopped(path, row) {
      if (current) states.set(current, view.state);
      for (const [name, state] of states) {
        const effects = setStoppedLine.of(name === path ? row : null);
        if (name === current) view.dispatch({ effects });
        else states.set(name, state.update({ effects }).state);
      }
    },
    // The text selected, as one string, its ranges a line apart.
    get selectedText() {
      const { state } = view;
      return state.selection.ranges.filter((range) => !range.empty)
        .map((range) => state.sliceDoc(range.from, range.to)).join(state.lineBreak);
    },
    // Replaces the selected text, as pasting does, or cutting with no text.
    replaceSelection(text) {
      view.dispatch(view.state.replaceSelection(text), { scrollIntoView: true,
        userEvent: text ? "input.paste" : "delete.cut" });
      view.focus();
    },
    // Runs a line command, as the Edit menu does: goToLine, comment, indent
    // or outdent.
    lineCommand(name) {
      view.focus();
      LINE_COMMANDS[name](view);
    },
    // Edit > Find… and Replace…, as Ctrl+F and Ctrl+R: the find bar.
    find() {
      openFinder(view, false);
    },
    replace() {
      openFinder(view, true);
    },
    // Edit > Find next and Find previous, as F3 and Shift+F3, with the find
    // bar open or closed.
    findNext() {
      view.focus();
      step(view, false);
    },
    findPrevious() {
      view.focus();
      step(view, true);
    },
    // The language mode changed: the code is coloured again.
    modeChanged() {
      blitz = blitzLanguage(commands, isExtended);
      const effects = language.reconfigure(blitz);
      view.dispatch({ effects });
      for (const [path, state] of states) {
        if (/\.bb$/i.test(path)) states.set(path, state.update({ effects }).state);
      }
    },
    // Shows or hides the line numbers, in every file.
    showLineNumbers(on) {
      numbered = on;
      const effects = numbers.reconfigure(on ? numberGutter : []);
      view.dispatch({ effects });
      for (const [path, state] of states) {
        if (state !== view.state) states.set(path, state.update({ effects }).state);
      }
    },
    // Measures the code font again, after index.html's --code-* change.
    refreshFont() {
      view.dispatch({ effects: font.reconfigure(EditorView.theme({})) });
    },
    // Marks numbers in the file shown, [[from, to], ...], for a moment: they
    // changed in the running program.
    flashNumbers(path, ranges) {
      if (path !== current || !ranges.length) return;
      view.dispatch({ effects: setLiveNumbers.of(ranges) });
      clearTimeout(this.flashTimer);
      this.flashTimer = setTimeout(() => view.dispatch({ effects: setLiveNumbers.of([]) }), 700);
    },
    goTo(row, col = 1) {
      const doc = view.state.doc;
      if (row < 1 || row > doc.lines) return;
      const line = doc.line(row);
      view.dispatch({ selection: { anchor: Math.min(line.from + col - 1, line.to) }, scrollIntoView: true });
      view.focus();
    },
    // Selects length characters from a row and column (from 1) in the file
    // shown, as a found match is.
    select(row, col, length) {
      const doc = view.state.doc;
      if (row < 1 || row > doc.lines) return;
      const line = doc.line(row);
      const from = Math.min(line.from + col - 1, line.to);
      // Focused first: focusing after would let the browser scroll the
      // selection into view itself, sideways. Then scrolled to the match's
      // start, mid-way down the editor.
      view.focus();
      const to = Math.min(from + length, doc.length);
      // The stretch from the line's start to the match's end: the line's
      // start stays in view when the match fits beside it.
      view.dispatch({ selection: { anchor: from, head: to },
        effects: EditorView.scrollIntoView(EditorSelection.range(line.from, to), { y: "center", x: "nearest" }) });
    },
    // The text of a file as the editor has it, which may not be saved yet,
    // or undefined for a file not opened.
    textOf(path) {
      const state = stateOf(path);
      return state ? state.doc.toString() : undefined;
    },
    // Blip's edit of a file: replaces from-to with insert, as one change of
    // its own, which Ctrl+Z undoes, in the file's own document whether or not
    // it is shown, opening it first with text if it is not open. The change
    // is saved as typing is (onChange). Returns the file's new text.
    edit(path, text, { from, to, insert }) {
      this.ensure(path, text);
      const spec = { changes: { from, to, insert }, userEvent: "blip", annotations: isolateHistory.of("full") };
      if (path === current) {
        view.dispatch(spec);
      } else {
        const transaction = states.get(path).update(spec);
        states.set(path, transaction.state);
        if (sharing) sharing.local(path, transaction.changes);
        onChange(path, states.get(path).doc.toString());
      }
      return stateOf(path).doc.toString();
    },
    // Marks the rows (from 1) of a file that Blip changed; no rows clears
    // them.
    markChanges(path, rows) {
      const state = stateOf(path);
      if (!state) return;
      const effects = setChangeMarks.of(rows);
      if (path === current) view.dispatch({ effects });
      else states.set(path, state.update({ effects }).state);
    },
    // Clears the marks of Blip's changes from every file.
    clearChangeMarks() {
      if (current) states.set(current, view.state);
      for (const [path, state] of states) {
        if (state.field(changeMarkField) === Decoration.none) continue;
        const effects = setChangeMarks.of([]);
        if (path === current) view.dispatch({ effects });
        else states.set(path, state.update({ effects }).state);
      }
    },
    // Replaces stretches of an open file, [{ from, to }], with text, as one
    // edit that Ctrl+Z undoes, as Replace in Files does. Returns whether the
    // file is open.
    replaceRanges(path, ranges, insert) {
      const spec = { changes: ranges.map(({ from, to }) => ({ from, to, insert })), userEvent: "input.replace.all",
        annotations: isolateHistory.of("full") };
      if (path === current) {
        view.dispatch(spec);
        return true;
      }
      const state = states.get(path);
      if (!state) return false;
      const transaction = state.update(spec);
      states.set(path, transaction.state);
      if (sharing) sharing.local(path, transaction.changes);
      return true;
    },
    focus() { view.focus(); },
    // Buddy sessions (buddies/session.js). share(handlers) starts sharing,
    // and share(null) stops: { textOf(path), local(path, changes),
    // selection(path, range) }.
    share(handlers) {
      sharing = handlers;
      if (!handlers) this.setReadOnly(false);
    },
    // The room's changes to a file, [{ from, to, insert }] from its text
    // before them, in each of its documents. Returns the file's text, or
    // undefined when it is not open.
    applyRemote(path, changes, text) {
      const state = stateOf(path);
      if (!state) return undefined;
      const spec = { changes, annotations: [fromRoom.of(true), Transaction.addToHistory.of(false)] };
      // A document that no longer matches the session's text takes it whole.
      const replace = { changes: { from: 0, to: state.doc.length, insert: text },
        annotations: [fromRoom.of(true), Transaction.addToHistory.of(false)] };
      let next;
      try {
        next = state.update(spec);
        if (next.state.doc.length !== text.length || (text.length < 200000 && next.state.doc.toString() !== text)) {
          next = state.update(replace);
        }
      } catch {
        next = state.update(replace);
      }
      if (path === current) view.dispatch(next);
      else states.set(path, next.state);
      return next.state.doc.toString();
    },
    // Other people's cursors in the file shown (binding.js's setCursors).
    showCursors(path, cursors) {
      if (path === current) view.dispatch({ effects: setCursors.of(cursors) });
    },
    // Scrolls a position of the file shown into view, if it is not in view:
    // following someone in a buddy session.
    reveal(position) {
      view.dispatch({ effects: EditorView.scrollIntoView(Math.min(position, view.state.doc.length), { y: "nearest" }) });
    },
    // Watchers read and do not type.
    setReadOnly(on) {
      if (readOnly === on) return;
      readOnly = on;
      const effects = editable.reconfigure(on ? EditorState.readOnly.of(true) : []);
      view.dispatch({ effects });
      for (const [path, state] of states) {
        if (path !== current) states.set(path, state.update({ effects }).state);
      }
    },
  };
}
